import React, { type PropsWithChildren, useRef } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  type TouchableOpacityProps,
  View,
  type ViewInstance,
} from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { useHovered } from '@garnish/core';
import { BodyText } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingDrawerNavLink = (props: Props) => {
  const { children, onPress, ...restProps } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const linkRef = useRef<ViewInstance>(null);
  const isHovered = useHovered(linkRef);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity {...restProps} accessibilityRole="link" activeOpacity={0.7} onPress={onPress}>
      <View ref={linkRef} style={styles.link}>
        {isHovered ? (
          <Animated.View
            entering={NAV_LINK_HOVER_ENTERING}
            exiting={NAV_LINK_HOVER_EXITING}
            pointerEvents="none"
            style={styles.hoverOverlay}
          />
        ) : undefined}

        <BodyText maxFontSizeMultiplier={1.2} sizeMatch={['16']} style={styles.label}>
          {children}
        </BodyText>
      </View>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NAV_LINK_HOVER_ENTERING = FadeIn.duration(theme.transitions.short);
const NAV_LINK_HOVER_EXITING = FadeOut.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    minHeight: 48,
    justifyContent: 'center',
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['4'],
    borderRadius: theme.radius.medium,
  },
  hoverOverlay: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.BLACK.ALMOST_TRANSPARENT,
  },
  label: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<Pick<TouchableOpacityProps, 'onPress'>>;
